import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy, limit, Timestamp, onSnapshot } from 'firebase/firestore';
import { apiHealthMonitor } from '../../services/apiHealthMonitor';
import AdminNetworkClubManager from './AdminNetworkClubManager';
import CalendarAdminCompact from './CalendarAdminCompact';
import AdminEmailInviteTool from './AdminEmailInviteTool';

// =============================================================================
// TYPES
// =============================================================================

interface DashboardStats {
  totalUsers: number;
  onlineUsers: number;
  totalPageViews: number;
  totalSessions: number;
  avgSessionDuration: number;
}

interface PageViewStat {
  page: string;
  views: number;
  uniqueUsers: number;
}

interface APIHealthStatus {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  error?: string;
  lastCheck?: Date;
  responseTime?: number;
}

interface ContentModerationReport {
  id: string;
  reportedBy: string;
  contentType: string;
  reason: string;
  status: 'pending' | 'reviewed' | 'resolved';
  timestamp: Date;
}

interface ConnectieFlowStats {
  totalFlows: number;
  activeFlows: number;
  completedFlows: number;
  avgDuration: number;
}

interface TokenExpiryInfo {
  service: string;
  expiresAt: Date;
  daysUntilExpiry: number;
  status: 'healthy' | 'warning' | 'expired';
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const AdminMonitoringDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    onlineUsers: 0,
    totalPageViews: 0,
    totalSessions: 0,
    avgSessionDuration: 0
  });

  const [topPages, setTopPages] = useState<PageViewStat[]>([]);
  const [apiHealth, setApiHealth] = useState<APIHealthStatus[]>([]);
  const [moderationReports, setModerationReports] = useState<ContentModerationReport[]>([]);
  const [connectieFlowStats, setConnectieFlowStats] = useState<ConnectieFlowStats>({
    totalFlows: 0,
    activeFlows: 0,
    completedFlows: 0,
    avgDuration: 0
  });
  const [tokenExpiries, setTokenExpiries] = useState<TokenExpiryInfo[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'schedule' | 'clubs' | 'invites' | 'pages' | 'api' | 'moderation' | 'tokens'>('overview');

  // =============================================================================
  // DATA LOADING
  // =============================================================================

  useEffect(() => {
    loadAllData();

    // Set up real-time listener for online users
    const unsubscribe = setupOnlineUsersListener();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      loadAllData();
    }, 30000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  const loadAllData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadDashboardStats(),
        loadTopPages(),
        loadAPIHealth(),
        loadModerationReports(),
        loadConnectieFlowStats(),
        loadTokenExpiries()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      // Total users
      const usersSnapshot = await getDocs(collection(db, 'userProfiles'));
      const totalUsers = usersSnapshot.size;

      // Online users (last activity < 5 minutes)
      const fiveMinutesAgo = new Date(Date.now() - 300000);
      const onlineQuery = query(
        collection(db, 'analytics_onlineUsers'),
        where('lastActivity', '>=', Timestamp.fromDate(fiveMinutesAgo)),
        where('isActive', '==', true)
      );
      const onlineSnapshot = await getDocs(onlineQuery);
      const onlineUsers = onlineSnapshot.size;

      // Page views (last 24 hours)
      const oneDayAgo = new Date(Date.now() - 86400000);
      const pageViewsQuery = query(
        collection(db, 'analytics_pageViews'),
        where('timestamp', '>=', Timestamp.fromDate(oneDayAgo))
      );
      const pageViewsSnapshot = await getDocs(pageViewsQuery);
      const totalPageViews = pageViewsSnapshot.size;

      // Sessions (last 24 hours)
      const sessionsQuery = query(
        collection(db, 'analytics_sessions'),
        where('startTime', '>=', Timestamp.fromDate(oneDayAgo))
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const totalSessions = sessionsSnapshot.size;

      // Avg session duration (simplified calculation)
      const avgSessionDuration = 450000; // Placeholder: 7.5 minutes

      setStats({
        totalUsers,
        onlineUsers,
        totalPageViews,
        totalSessions,
        avgSessionDuration
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const loadTopPages = async () => {
    try {
      const oneDayAgo = new Date(Date.now() - 86400000);
      const pageViewsQuery = query(
        collection(db, 'analytics_pageViews'),
        where('timestamp', '>=', Timestamp.fromDate(oneDayAgo))
      );
      const snapshot = await getDocs(pageViewsQuery);

      // Count page views
      const pageCounts: { [key: string]: { views: number; users: Set<string> } } = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const page = data.page || 'unknown';
        const userId = data.userId;

        if (!pageCounts[page]) {
          pageCounts[page] = { views: 0, users: new Set() };
        }
        pageCounts[page].views++;
        pageCounts[page].users.add(userId);
      });

      // Convert to array and sort
      const topPagesList: PageViewStat[] = Object.entries(pageCounts)
        .map(([page, data]) => ({
          page,
          views: data.views,
          uniqueUsers: data.users.size
        }))
        .sort((a, b) => b.views - a.views)
        .slice(0, 10);

      setTopPages(topPagesList);
    } catch (error) {
      console.error('Error loading top pages:', error);
    }
  };

  const loadAPIHealth = async () => {
    try {
      const [openai, gemini, elevenlabs] = await Promise.all([
        apiHealthMonitor.checkAPI('openai'),
        apiHealthMonitor.checkAPI('gemini'),
        apiHealthMonitor.checkAPI('elevenlabs')
      ]);

      setApiHealth([
        {
          service: 'OpenAI',
          status: openai.status,
          error: openai.error,
          lastCheck: openai.timestamp,
          responseTime: openai.responseTime
        },
        {
          service: 'Gemini',
          status: gemini.status,
          error: gemini.error,
          lastCheck: gemini.timestamp,
          responseTime: gemini.responseTime
        },
        {
          service: 'ElevenLabs',
          status: elevenlabs.status,
          error: elevenlabs.error,
          lastCheck: elevenlabs.timestamp,
          responseTime: elevenlabs.responseTime
        }
      ]);
    } catch (error) {
      console.error('Error loading API health:', error);
    }
  };

  const loadModerationReports = async () => {
    try {
      const reportsQuery = query(
        collection(db, 'contentReports'),
        where('status', 'in', ['pending', 'reviewed']),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(reportsQuery);

      const reports: ContentModerationReport[] = snapshot.docs.map(doc => ({
        id: doc.id,
        reportedBy: doc.data().reportedBy || 'anonymous',
        contentType: doc.data().contentType || 'unknown',
        reason: doc.data().reason || 'No reason provided',
        status: doc.data().status || 'pending',
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));

      setModerationReports(reports);
    } catch (error) {
      console.error('Error loading moderation reports:', error);
      setModerationReports([]);
    }
  };

  const loadConnectieFlowStats = async () => {
    try {
      const flowsSnapshot = await getDocs(collection(db, 'connectionFlows'));
      const totalFlows = flowsSnapshot.size;

      let activeFlows = 0;
      let completedFlows = 0;

      flowsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.status === 'active' || data.status === 'in_progress') {
          activeFlows++;
        } else if (data.status === 'completed') {
          completedFlows++;
        }
      });

      setConnectieFlowStats({
        totalFlows,
        activeFlows,
        completedFlows,
        avgDuration: 1200000 // Placeholder: 20 minutes
      });
    } catch (error) {
      console.error('Error loading ConnectieFlow stats:', error);
    }
  };

  const loadTokenExpiries = async () => {
    try {
      const tokensSnapshot = await getDocs(collection(db, 'externalTokens'));
      
      const tokens: TokenExpiryInfo[] = tokensSnapshot.docs.map(doc => {
        const data = doc.data();
        const expiresAt = data.expiresAt?.toDate() || new Date();
        const daysUntilExpiry = Math.floor((expiresAt.getTime() - Date.now()) / 86400000);
        
        let status: 'healthy' | 'warning' | 'expired' = 'healthy';
        if (daysUntilExpiry < 0) {
          status = 'expired';
        } else if (daysUntilExpiry < 7) {
          status = 'warning';
        }

        return {
          service: data.service || doc.id,
          expiresAt,
          daysUntilExpiry,
          status
        };
      });

      setTokenExpiries(tokens);
    } catch (error) {
      console.error('Error loading token expiries:', error);
      setTokenExpiries([]);
    }
  };

  const setupOnlineUsersListener = () => {
    const fiveMinutesAgo = new Date(Date.now() - 300000);
    const onlineQuery = query(
      collection(db, 'analytics_onlineUsers'),
      where('lastActivity', '>=', Timestamp.fromDate(fiveMinutesAgo)),
      where('isActive', '==', true)
    );

    return onSnapshot(onlineQuery, (snapshot) => {
      setStats(prev => ({
        ...prev,
        onlineUsers: snapshot.size
      }));
    });
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Monitoring dashboard laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Monitoring Dashboard</h1>
          <p className="text-gray-600 mt-1">Real-time overzicht van app health en gebruiksstatistieken</p>
        </div>
        <button
          onClick={loadAllData}
          disabled={refreshing}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-2"
        >
          {refreshing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Refreshing...</span>
            </>
          ) : (
            <>
              <span>🔄</span>
              <span>Refresh</span>
            </>
          )}
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          title="Totaal Gebruikers"
          value={stats.totalUsers}
          icon="👥"
          color="blue"
        />
        <StatCard
          title="Online Nu"
          value={stats.onlineUsers}
          icon="🟢"
          color="green"
          subtitle="Actief in de afgelopen 5 min"
        />
        <StatCard
          title="Page Views (24u)"
          value={stats.totalPageViews}
          icon="📄"
          color="purple"
        />
        <StatCard
          title="Sessies (24u)"
          value={stats.totalSessions}
          icon="⏱️"
          color="orange"
        />
        <StatCard
          title="Gem. Sessieduur"
          value={formatDuration(stats.avgSessionDuration)}
          icon="⌚"
          color="indigo"
        />
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overzicht', icon: '📊' },
            { id: 'schedule', label: 'Studio', icon: '📅' },
            { id: 'clubs', label: 'Netwerkclubs', icon: '🏢' },
            { id: 'invites', label: 'Uitnodigingen', icon: '✉️' },
            { id: 'pages', label: 'Pagina\'s', icon: '📄' },
            { id: 'api', label: 'API Health', icon: '🔌' },
            { id: 'moderation', label: 'Moderatie', icon: '🚨' },
            { id: 'tokens', label: 'Tokens', icon: '🔑' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Pages */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">📈 Meest Bezochte Pagina's (24u)</h3>
            {topPages.length > 0 ? (
              <div className="space-y-3">
                {topPages.map((page, index) => (
                  <div key={page.page} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-400 w-6">{index + 1}.</span>
                      <div>
                        <p className="font-medium">{page.page}</p>
                        <p className="text-xs text-gray-500">{page.uniqueUsers} unieke gebruikers</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">{page.views} views</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Geen data beschikbaar</p>
            )}
          </div>

          {/* ConnectieFlow Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">🔗 ConnectieFlow Statistieken</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Totaal Flows</span>
                <span className="font-semibold text-xl">{connectieFlowStats.totalFlows}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Actief Nu</span>
                <span className="font-semibold text-xl text-green-600">{connectieFlowStats.activeFlows}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Voltooid</span>
                <span className="font-semibold text-xl text-blue-600">{connectieFlowStats.completedFlows}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Gem. Duur</span>
                <span className="font-semibold text-xl">{formatDuration(connectieFlowStats.avgDuration)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'schedule' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">📅 Studio Kalender</h3>
          <CalendarAdminCompact />
        </div>
      )}

      {activeTab === 'clubs' && (
        <AdminNetworkClubManager />
      )}

      {activeTab === 'invites' && (
        <div style={{ maxWidth: 600 }}>
          <AdminEmailInviteTool />
        </div>
      )}

      {activeTab === 'pages' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">📄 Gedetailleerde Pagina Statistieken</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pagina</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unieke Gebruikers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topPages.map(page => (
                  <tr key={page.page}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">{page.page}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{page.views}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{page.uniqueUsers}</td>
                    <td className="px-6 py-4 whitespace-nowrap">-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'api' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {apiHealth.map(api => (
            <div key={api.service} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{api.service}</h3>
                <StatusBadge status={api.status} />
              </div>
              {api.responseTime && (
                <p className="text-sm text-gray-600 mb-2">
                  Response tijd: <span className="font-semibold">{api.responseTime}ms</span>
                </p>
              )}
              {api.lastCheck && (
                <p className="text-xs text-gray-500">
                  Laatst gecheckt: {api.lastCheck.toLocaleTimeString('nl-NL')}
                </p>
              )}
              {api.error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-xs text-red-700">{api.error}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'moderation' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">🚨 Content Moderatie Rapporten</h3>
          {moderationReports.length > 0 ? (
            <div className="space-y-4">
              {moderationReports.map(report => (
                <div key={report.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium">{report.contentType}</p>
                      <p className="text-sm text-gray-600">{report.reason}</p>
                    </div>
                    <span className={`
                      px-2 py-1 text-xs rounded
                      ${report.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : ''}
                      ${report.status === 'reviewed' ? 'bg-blue-100 text-blue-800' : ''}
                      ${report.status === 'resolved' ? 'bg-green-100 text-green-800' : ''}
                    `}>
                      {report.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Gemeld door: {report.reportedBy} • {report.timestamp.toLocaleString('nl-NL')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">Geen openstaande moderatie rapporten</p>
          )}
        </div>
      )}

      {activeTab === 'tokens' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">🔑 External API Token Expiries</h3>
          {tokenExpiries.length > 0 ? (
            <div className="space-y-4">
              {tokenExpiries.map(token => (
                <div key={token.service} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{token.service}</p>
                      <p className="text-sm text-gray-600">
                        Verloopt op: {token.expiresAt.toLocaleDateString('nl-NL')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`
                        text-lg font-semibold
                        ${token.status === 'healthy' ? 'text-green-600' : ''}
                        ${token.status === 'warning' ? 'text-yellow-600' : ''}
                        ${token.status === 'expired' ? 'text-red-600' : ''}
                      `}>
                        {token.daysUntilExpiry >= 0 ? `${token.daysUntilExpiry} dagen` : 'Verlopen'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {token.status === 'expired' ? '❌ Vernieuwen!' : token.status === 'warning' ? '⚠️ Bijna verlopen' : '✅ OK'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">Geen token informatie beschikbaar</p>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    indigo: 'bg-indigo-50 text-indigo-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600">{title}</p>
        <span className={`text-2xl ${colorClasses[color as keyof typeof colorClasses]}`}>{icon}</span>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
};

interface StatusBadgeProps {
  status: 'healthy' | 'degraded' | 'down';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const statusConfig = {
    healthy: { label: '✅ Healthy', className: 'bg-green-100 text-green-800' },
    degraded: { label: '⚠️ Degraded', className: 'bg-yellow-100 text-yellow-800' },
    down: { label: '❌ Down', className: 'bg-red-100 text-red-800' }
  };

  const config = statusConfig[status];

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.className}`}>
      {config.label}
    </span>
  );
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export default AdminMonitoringDashboard;
